/**
 * Firestore is also considered as integration as it helps a lot in testing
 */
import { Page, filterUndefinedFields, isDefined } from "@zickko/shared";
import {
  DocumentData,
  OrderByDirection,
  QueryDocumentSnapshot,
  SetOptions,
  WhereFilterOp,
  collection as _collection,
  deleteDoc as _deleteDoc,
  doc as _doc,
  getDoc as _getDoc,
  getDocs as _getDocs,
  limit as _limit,
  orderBy as _orderBy,
  query as _query,
  setDoc as _setDoc,
  startAfter as _startAfter,
  where as _where,
  getCountFromServer,
} from "firebase/firestore";
import { firebaseFirestore } from "../services/firebaseFirestore";

export type Cursor = QueryDocumentSnapshot<DocumentData>;

/**
 *
 * @param path This API does not work with keys, instead the path to the document or the collection in firestore should be provided
 * @returns
 */

const documentRef = (path: string) => {
  return _doc(firebaseFirestore, path);
};

const collectionRef = (path: string) => {
  return _collection(firebaseFirestore, path);
};

const getDoc = async <T>(path: string): Promise<T | null> => {
  const document = await _getDoc(documentRef(path));
  if (document.exists()) return document.data() as T;
  return null;
};

const setDoc = async <T>(path: string, obj: T, opts?: SetOptions) => {
  const safeObj = filterUndefinedFields(obj);
  return _setDoc(documentRef(path), safeObj, opts ?? {});
};

const mergeDoc = async <T>(path: string, obj: Partial<T>) => {
  const safeObj = filterUndefinedFields(obj);
  return _setDoc(documentRef(path), safeObj, { merge: true });
};

const deleteDoc = async (path: string) => {
  return _deleteDoc(documentRef(path));
};

// https://firebase.google.com/docs/firestore/query-data/queries
const page = async <T>(args: {
  path: string;
  where?: [Extract<keyof T, string>, WhereFilterOp, unknown][];
  orderByField: Extract<keyof T, string>;
  orderByDirection: OrderByDirection;
  limit?: number;
  cursor?: Cursor;
}): Promise<Page<T>> => {
  //

  const { path, orderByField, orderByDirection, limit = 100, cursor, where } = args;

  // create query
  let query = _query(collectionRef(path), _orderBy(orderByField, orderByDirection), _limit(limit));

  // cursor
  if (cursor) query = _query(query, _startAfter(cursor));

  // where clause and the value is defined
  if (where) {
    if (where.length > 3) {
      console.error(
        `Cannot have more than 3 items in where clause, please remodel the data to be more optimistic for querying.`
      );
      return {
        list: [],
        cursor: null,
      };
    }
    for (const w of where) {
      if (w && isDefined(w[2])) query = _query(query, _where(w[0], w[1], w[2]));
    }
  }

  // get snapshot
  const snapshot = await _getDocs(query);

  // empty list
  if (snapshot.empty) return { list: [], cursor: null };

  // return without cursor
  const list = snapshot.docs.map((doc) => doc.data() as T);
  if (snapshot.size < limit) return { list, cursor: null };

  // return with cursor
  return { list, cursor: snapshot.docs[snapshot.docs.length - 1] };
};

const query = async <T>(args: {
  path: string;
  where?: [Extract<keyof T, string>, WhereFilterOp, unknown][];
  orderByField: Extract<keyof T, string>;
  orderByDirection: OrderByDirection;
  limit?: number;
}): Promise<T[] | undefined> => {
  //
  const { path, orderByField, orderByDirection, where } = args;

  // set max limit
  const limit = args.limit && args.limit < 20 ? args.limit : 20;

  // create query
  let query = _query(collectionRef(path), _orderBy(orderByField, orderByDirection), _limit(limit));

  // where clause and the value is defined
  if (where) {
    if (where.length > 3) {
      console.error(
        `Cannot have more than 3 items in where clause, please remodel the data to be more optimistic for querying.`
      );
      return;
    }
    for (const w of where) {
      if (w && isDefined(w[2])) query = _query(query, _where(w[0], w[1], w[2]));
    }
  }

  // get snapshot
  const snapshot = await _getDocs(query);
  if (snapshot.empty) return;

  return snapshot.docs.map((doc) => doc.data() as T);
};

const countAll = async <T>(args: { path: string }): Promise<number | undefined> => {
  //
  const { path } = args;
  let snapshot = await getCountFromServer(collectionRef(path));
  return snapshot.data().count;
};

export const firestoreApi = {
  documentRef,
  collectionRef,
  getDoc,
  setDoc,
  mergeDoc,
  deleteDoc,
  page,
  query,
  countAll,
};
